// <pos-customer-menu/> — 顧客 QR 點餐菜單頁（手機視角）
// 路徑：#/order/:slug/:table
// 從 location.hash 解出 slug + table，呼叫 fetchPublicMenu
// 購物車放 sessionStorage（同桌重整不掉單；關分頁就清）
// see src/designs/customer-menu.html + docs/QR_DEMO_SPRINT.md

import { fetchPublicMenu } from '../lib/api.js';
import { t, lang, setLang, langToggleHtml, LANG_TOGGLE_CSS } from '../lib/i18n.js';
import { captureLongTokenFromUrl } from '../lib/qr-session-store.js';

const CART_KEY = 'pos-qr-cart';

function parseRouteParams() {
  // #/order/laopo-coffee/1 → { slug, table }    （?qrt=... 在後面，正則用 [^\/?] 排除）
  const m = location.hash.match(/^#\/order\/([^\/?]+)\/([^\/?]+)/);
  return m ? { slug: decodeURIComponent(m[1]), table: decodeURIComponent(m[2]) } : {};
}

function readCart() {
  try {
    const raw = sessionStorage.getItem(CART_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function writeCart(cart) {
  try { sessionStorage.setItem(CART_KEY, JSON.stringify(cart)); } catch {}
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
}

class PosCustomerMenu extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = {
      loading: true,
      error: null,
      tenant: null,
      store: null,
      categories: [],
      items: [],
      activeCategoryId: null,  // null = 全部
      cart: {},  // { [menu_item_id]: qty }
    };
  }

  async connectedCallback() {
    this.state.cart = readCart();
    this.render();
    await this.load();
  }

  async load() {
    const { slug, table } = parseRouteParams();
    // C8 方案 A：顧客掃 QR 進來 → URL 帶 ?qrt=<long_token> → cache 起來
    // checkout 送單前再換 session token（短期 5min）
    if (slug && table) captureLongTokenFromUrl(slug, table);
    if (!slug) {
      this.state.loading = false;
      this.state.error = t('網址格式錯誤：請從店家 QR 掃碼進入');
      this.render();
      return;
    }
    try {
      const data = await fetchPublicMenu(slug);
      this.state.tenant = data.tenant;
      this.state.store = data.store;
      this.state.categories = data.categories || [];
      this.state.items = data.items || [];
    } catch (err) {
      this.state.error = (lang() === 'zh' ? err.message_zh : err.message_en) || err.message || t('菜單載入失敗');
    } finally {
      this.state.loading = false;
      this.render();
    }
  }

  // ─── Cart helpers ───
  addItem(itemId) {
    this.state.cart[itemId] = (this.state.cart[itemId] || 0) + 1;
    writeCart(this.state.cart);
    this.render();
  }
  removeItem(itemId) {
    if (!this.state.cart[itemId]) return;
    this.state.cart[itemId] -= 1;
    if (this.state.cart[itemId] <= 0) delete this.state.cart[itemId];
    writeCart(this.state.cart);
    this.render();
  }
  clearCart() {
    if (!Object.keys(this.state.cart).length) return;
    if (!confirm(t('確定清空購物車嗎？'))) return;
    this.state.cart = {};
    sessionStorage.removeItem(CART_KEY);
    this.render();
  }

  cartSummary() {
    const entries = Object.entries(this.state.cart);
    let totalQty = 0, totalAmount = 0;
    for (const [id, qty] of entries) {
      const item = this.state.items.find(i => i.id === id);
      if (!item) continue;
      totalQty += qty;
      totalAmount += Number(item.price) * qty;
    }
    return { totalQty, totalAmount };
  }

  render() {
    const { loading, error, tenant, categories, items, activeCategoryId, cart } = this.state;
    const { table } = parseRouteParams();
    const { totalQty, totalAmount } = this.cartSummary();

    const visibleItems = activeCategoryId
      ? items.filter(i => i.category_id === activeCategoryId)
      : items;

    this.shadowRoot.innerHTML = `
      <style>${this.styles()}</style>

      ${error ? `
        <div class="full-msg">
          <div class="full-msg__icon">⚠</div>
          <div>${escapeHtml(error)}</div>
        </div>
      ` : loading ? `
        <div class="full-msg"><div>${t('載入中…')}</div></div>
      ` : `
        <header class="shop-header">
          <div class="shop-header__row">
            <div class="shop-header__name">${escapeHtml(tenant?.display_name || t('店家'))}</div>
            <div class="shop-header__table">${t('桌號')} <strong>${escapeHtml(table || '-')}</strong></div>
          </div>
          <div class="shop-header__hint">${t('掃描點餐 · 店員確認後準備')}</div>${langToggleHtml()}
        </header>

        <nav class="cat-tabs" role="tablist">
          <button class="cat-tab ${!activeCategoryId ? 'is-active' : ''}" data-cat="">${t('全部')}</button>
          ${categories.map(c => `
            <button class="cat-tab ${activeCategoryId === c.id ? 'is-active' : ''}" data-cat="${escapeHtml(c.id)}">
              ${escapeHtml(c.name)}
            </button>
          `).join('')}
        </nav>

        <section class="menu-list">
          ${visibleItems.length === 0 ? `
            <div class="empty">${t('目前沒有品項')}</div>
          ` : visibleItems.map(i => {
            const qty = cart[i.id] || 0;
            // 主廚推薦 badge（小倉效期驅動，see architecture.md#L1121）
            // 高 urgency 顯示金色「⭐ 主廚推薦」；中 urgency 顯示銀色「推薦」
            // 不洩漏 reason / expiry（顧客看了會疑慮，那是內部資訊）
            const urg = i.recommendation_urgency;
            const recBadge = urg === 'high'
              ? `<span class="rec-badge rec-badge--high" aria-label="${t('主廚推薦')}">⭐ ${t('主廚推薦')}</span>`
              : urg === 'medium'
                ? `<span class="rec-badge rec-badge--medium">${t('推薦')}</span>`
                : '';
            return `
              <article class="menu-item ${urg ? 'menu-item--recommended' : ''}">
                <div class="menu-item__main">
                  <div class="menu-item__name">${escapeHtml(i.name)} ${recBadge}</div>
                  ${i.description ? `<div class="menu-item__desc">${escapeHtml(i.description)}</div>` : ''}
                  <div class="menu-item__price">NT$ ${i.price}</div>
                </div>
                <div class="menu-item__actions">
                  ${qty > 0 ? `
                    <div class="menu-item__qty">
                      <button class="qty-btn" data-act="minus" data-id="${i.id}">−</button>
                      <span class="qty-value">${qty}</span>
                      <button class="qty-btn" data-act="plus" data-id="${i.id}">+</button>
                    </div>
                  ` : `
                    <button class="btn-add" data-act="plus" data-id="${i.id}" aria-label="${t('加入')}${escapeHtml(i.name)}">+</button>
                  `}
                </div>
              </article>
            `;
          }).join('')}
        </section>

        <div class="cart-bar ${totalQty === 0 ? 'is-empty' : ''}">
          <div>
            <div class="cart-bar__summary">
              ${t('共')} <strong>${totalQty}</strong> ${t('項')}
              ${totalQty > 0 ? `<button class="cart-bar__clear" data-act="clear" aria-label="${t('清空購物車')}">${t('清空')}</button>` : ''}
            </div>
            <div class="cart-bar__total">NT$ ${totalAmount}</div>
          </div>
          <button class="btn-checkout" data-act="checkout" ${totalQty === 0 ? 'disabled' : ''}>
            ${t('前往送單')}
            <span aria-hidden="true">→</span>
          </button>
        </div>
      `}
    `;

    this.wireEvents();
  }

  wireEvents() {
    const root = this.shadowRoot;
    root.querySelectorAll('.cat-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this.state.activeCategoryId = btn.dataset.cat || null;
        this.render();
      });
    });
    root.querySelectorAll('[data-act]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const act = btn.dataset.act;
        const id = btn.dataset.id;
        if (act === 'lang-toggle') { setLang(lang() === 'zh' ? 'en' : 'zh'); this.render(); }
        else if (act === 'plus') this.addItem(id);
        else if (act === 'minus') this.removeItem(id);
        else if (act === 'clear') this.clearCart();
        else if (act === 'checkout') {
          const { slug, table } = parseRouteParams();
          location.hash = `#/order/${encodeURIComponent(slug)}/${encodeURIComponent(table)}/checkout`;
        }
      });
    });
  }

  styles() {
    return `
      :host { display: block; font-family: var(--font-sans); background: var(--bg-base); color: var(--fg-base); min-height: 100vh; padding-bottom: 96px; }
      .full-msg { padding: var(--s-16) var(--s-5); text-align: center; color: var(--fg-muted); display: grid; gap: var(--s-3); place-content: center; min-height: 60vh; }
      .full-msg__icon { font-size: 3rem; opacity: .5; }

      ${LANG_TOGGLE_CSS}
      .lang-toggle { top: 0.85rem; right: 1rem; }
      .shop-header { position: sticky; top: 0; z-index: 20; background: linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-elevated) 100%); padding: var(--s-4) var(--s-5) var(--s-3); border-bottom: 1px solid var(--border); box-shadow: var(--shadow-sm); }
      .shop-header__row { display: flex; justify-content: space-between; align-items: baseline; }
      .shop-header__name { font-family: var(--font-display); font-size: 1.4rem; color: var(--brand-primary); letter-spacing: 1px; }
      .shop-header__table { color: var(--fg-muted); font-size: .85rem; }
      .shop-header__table strong { color: var(--brand-accent); font-weight: 600; }
      .shop-header__hint { margin-top: var(--s-1); color: var(--fg-dim); font-size: .75rem; }

      .cat-tabs { position: sticky; top: 72px; z-index: 15; display: flex; gap: var(--s-2); overflow-x: auto; overflow-y: hidden; padding: var(--s-3) var(--s-5); background: var(--bg-base); border-bottom: 1px solid var(--border); scrollbar-width: none; }
      .cat-tabs::-webkit-scrollbar { display: none; }
      .cat-tab { flex: 0 0 auto; padding: var(--s-2) var(--s-4); background: var(--bg-elevated); color: var(--fg-muted); border: 1px solid var(--border); border-radius: var(--r-full); font-size: .9rem; white-space: nowrap; cursor: pointer; }
      .cat-tab.is-active { background: var(--brand-primary); color: var(--fg-on-brand); border-color: var(--brand-primary); font-weight: 600; }

      .menu-list { padding: var(--s-4) var(--s-5); display: grid; gap: var(--s-3); }
      .menu-item { display: grid; grid-template-columns: 1fr auto; gap: var(--s-4); padding: var(--s-4); background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-lg); }
      .menu-item--recommended { border-color: #f59e0b; background: linear-gradient(135deg, var(--bg-elevated) 60%, rgba(245,158,11,.06) 100%); }
      .rec-badge { display: inline-block; margin-left: var(--s-2); padding: 2px 8px; border-radius: var(--r-sm); font-size: 11px; font-weight: 700; vertical-align: middle; letter-spacing: .5px; }
      .rec-badge--high { background: linear-gradient(135deg, #f59e0b, #dc2626); color: white; box-shadow: 0 2px 6px rgba(245,158,11,.3); }
      .rec-badge--medium { background: rgba(245,158,11,.15); color: #d97706; border: 1px solid #f59e0b; }
      .menu-item__main { min-width: 0; }
      .menu-item__name { font-size: 1.05rem; font-weight: 600; color: var(--fg-base); }
      .menu-item__desc { margin-top: var(--s-1); font-size: .8rem; color: var(--fg-muted); line-height: 1.5; }
      .menu-item__price { margin-top: var(--s-2); color: var(--brand-primary); font-weight: 600; font-family: var(--font-mono); font-size: .95rem; }
      .menu-item__actions { display: flex; align-items: center; }
      .btn-add { width: 44px; height: 44px; border-radius: var(--r-full); background: var(--brand-primary); color: var(--fg-on-brand); border: none; font-size: 1.4rem; font-weight: 700; cursor: pointer; transition: transform var(--dur-fast) var(--ease); }
      .btn-add:active { transform: scale(.9); }
      .menu-item__qty { display: flex; align-items: center; gap: var(--s-2); padding: var(--s-1) var(--s-2); background: var(--bg-base); border-radius: var(--r-full); border: 1px solid var(--brand-primary); }
      .qty-btn { width: 32px; height: 32px; background: var(--brand-primary); color: var(--fg-on-brand); border: none; border-radius: var(--r-full); font-size: 1.1rem; font-weight: 700; cursor: pointer; }
      .qty-value { font-variant-numeric: tabular-nums; color: var(--fg-base); font-weight: 600; min-width: 20px; text-align: center; }

      .cart-bar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 30; padding: var(--s-3) var(--s-5) calc(var(--s-3) + env(safe-area-inset-bottom)); background: var(--bg-surface); border-top: 1px solid var(--brand-primary); box-shadow: 0 -8px 24px rgba(232, 88, 12, 0.15); display: flex; justify-content: space-between; align-items: center; gap: var(--s-3); }
      .cart-bar.is-empty { opacity: .5; }
      .cart-bar.is-empty .btn-checkout { cursor: not-allowed; }
      .cart-bar__summary { color: var(--fg-muted); font-size: .85rem; display: flex; align-items: center; gap: var(--s-3); }
      .cart-bar__summary strong { color: var(--brand-accent); font-weight: 600; }
      .cart-bar__clear { background: transparent; border: 1px solid var(--border); color: var(--fg-muted); padding: 2px var(--s-2); border-radius: var(--r-sm); font-size: .7rem; cursor: pointer; font-family: inherit; }
      .cart-bar__clear:hover { color: var(--danger); border-color: var(--danger); }
      .cart-bar__total { color: var(--brand-primary); font-family: var(--font-mono); font-size: 1.1rem; font-weight: 700; display: block; margin-top: 2px; }
      .btn-checkout { flex: 0 0 auto; padding: 0 var(--s-5); height: 52px; background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-accent) 100%); color: var(--fg-on-brand); border: none; border-radius: var(--r-lg); font-size: 1rem; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: var(--s-2); }
      .btn-checkout:active { opacity: .85; }
      .btn-checkout:disabled { cursor: not-allowed; }

      .empty { padding: var(--s-12) var(--s-5); text-align: center; color: var(--fg-dim); }
    `;
  }
}

customElements.define('pos-customer-menu', PosCustomerMenu);
